/**
 * Command Center building blocks. Every new screen (Home, Traffic, Affiliates,
 * Work, Team) is made of these, so the whole app reads as one product:
 * glass cards on the night sky, big friendly numbers, plain words.
 */
import { useState, type ComponentType, type ReactNode } from "react";
import { Link } from "react-router-dom";
import { ArrowDownRight, ArrowUpRight, Check, Minus } from "lucide-react";
import { cn } from "@/lib/utils";

// ─── Formatting ──────────────────────────────────────────────────────────────

/** "$1,240" / "$931" / "$12.5k" (dollars, not cents). */
export function usd(n: number | string | null | undefined, opts: { compact?: boolean; cents?: boolean } = {}): string {
  const v = Number(n ?? 0) || 0;
  if (opts.compact && Math.abs(v) >= 10_000) return `$${(v / 1000).toFixed(v >= 100_000 ? 0 : 1)}k`;
  return new Intl.NumberFormat("en-US", {
    style: "currency", currency: "USD",
    minimumFractionDigits: opts.cents ? 2 : 0, maximumFractionDigits: opts.cents ? 2 : 0,
  }).format(v);
}

export function num(n: number | string | null | undefined): string {
  return new Intl.NumberFormat("en-US").format(Number(n ?? 0) || 0);
}

export function pct(n: number | null | undefined, digits = 0): string {
  if (n == null || !Number.isFinite(n)) return "–";
  return `${(n * 100).toFixed(digits)}%`;
}

/** Change from `prev` to `now` as a ratio, or null when there's nothing to compare. */
export function change(now: number, prev: number): number | null {
  if (!prev && !now) return null;
  if (!prev) return 1;
  return (now - prev) / prev;
}

export function dayLabel(iso: string | null | undefined, opts: Intl.DateTimeFormatOptions = { month: "short", day: "numeric" }): string {
  if (!iso) return "";
  const d = /^\d{4}-\d{2}-\d{2}$/.test(iso) ? new Date(`${iso}T12:00:00`) : new Date(iso);
  return d.toLocaleDateString(undefined, opts);
}

/** "today" / "tomorrow" / "Thu" / "Oct 30" / "3 days late". */
export function dueLabel(iso: string | null | undefined, today = new Date()): { text: string; tone: "late" | "today" | "soon" | "later" | "none" } {
  if (!iso) return { text: "no date", tone: "none" };
  const d = new Date(iso);
  const start = (x: Date) => new Date(x.getFullYear(), x.getMonth(), x.getDate()).getTime();
  const diff = Math.round((start(d) - start(today)) / 86400_000);
  if (diff < 0) return { text: diff === -1 ? "yesterday" : `${-diff} days late`, tone: "late" };
  if (diff === 0) return { text: "today", tone: "today" };
  if (diff === 1) return { text: "tomorrow", tone: "soon" };
  if (diff < 7) return { text: d.toLocaleDateString(undefined, { weekday: "long" }), tone: "soon" };
  return { text: d.toLocaleDateString(undefined, { month: "short", day: "numeric" }), tone: "later" };
}

export function timeAgo(iso: string | null | undefined): string {
  if (!iso) return "";
  const s = (Date.now() - new Date(iso).getTime()) / 1000;
  if (s < 60) return "just now";
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  if (s < 86400 * 7) return `${Math.floor(s / 86400)}d ago`;
  return new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric" });
}

export function firstName(name: string | null | undefined): string {
  const f = String(name ?? "").trim().split(/\s+/)[0] ?? "";
  return f ? f.charAt(0).toUpperCase() + f.slice(1) : "";
}

// ─── Colour for a source / category ──────────────────────────────────────────

const DOT: Record<string, string> = {
  pink: "bg-pink-400", violet: "bg-violet-400", blue: "bg-sky-400", amber: "bg-amber-400", zinc: "bg-zinc-400",
  emerald: "bg-emerald-400", orange: "bg-orange-400", indigo: "bg-indigo-400", sky: "bg-sky-400", fuchsia: "bg-fuchsia-400",
  teal: "bg-teal-400", rose: "bg-rose-400", yellow: "bg-yellow-400", stone: "bg-stone-400", lime: "bg-lime-400",
  slate: "bg-slate-400", magenta: "bg-fuchsia-400", aqua: "bg-cyan-400", green: "bg-emerald-400", red: "bg-red-400",
  brand: "bg-indigo-400",
};
export function dotClass(color: string | null | undefined): string {
  return DOT[String(color ?? "")] ?? "bg-indigo-400";
}
const SOFT: Record<string, string> = {
  pink: "bg-pink-500/12 text-pink-300 ring-pink-500/25", violet: "bg-violet-500/12 text-violet-300 ring-violet-500/25",
  blue: "bg-sky-500/12 text-sky-300 ring-sky-500/25", amber: "bg-amber-500/12 text-amber-300 ring-amber-500/25",
  zinc: "bg-zinc-500/12 text-zinc-300 ring-zinc-500/25", emerald: "bg-emerald-500/12 text-emerald-300 ring-emerald-500/25",
  orange: "bg-orange-500/12 text-orange-300 ring-orange-500/25", indigo: "bg-indigo-500/12 text-indigo-300 ring-indigo-500/25",
  sky: "bg-sky-500/12 text-sky-300 ring-sky-500/25", fuchsia: "bg-fuchsia-500/12 text-fuchsia-300 ring-fuchsia-500/25",
  teal: "bg-teal-500/12 text-teal-300 ring-teal-500/25", rose: "bg-rose-500/12 text-rose-300 ring-rose-500/25",
  yellow: "bg-yellow-500/12 text-yellow-300 ring-yellow-500/25", stone: "bg-stone-500/12 text-stone-300 ring-stone-500/25",
  lime: "bg-lime-500/12 text-lime-300 ring-lime-500/25", slate: "bg-slate-500/12 text-slate-300 ring-slate-500/25",
};
export function softClass(color: string | null | undefined): string {
  return SOFT[String(color ?? "")] ?? SOFT.indigo!;
}

// ─── Layout pieces ───────────────────────────────────────────────────────────

export function PageHeader({
  eyebrow, title, subtitle, actions, className,
}: { eyebrow?: ReactNode; title: ReactNode; subtitle?: ReactNode; actions?: ReactNode; className?: string }) {
  return (
    <div className={cn("flex flex-col gap-3 lg:flex-row lg:items-end lg:justify-between", className)}>
      <div className="min-w-0">
        {eyebrow && <div className="mb-1 text-[11px] font-semibold uppercase tracking-[0.14em] text-indigo-300/80">{eyebrow}</div>}
        <h1 className="text-2xl font-semibold tracking-tight text-zinc-50 sm:text-[28px]">{title}</h1>
        {subtitle && <p className="mt-1 max-w-2xl text-sm text-zinc-400">{subtitle}</p>}
      </div>
      {actions && <div className="flex flex-wrap items-center gap-2 lg:shrink-0 lg:justify-end">{actions}</div>}
    </div>
  );
}

export function Card({ children, className, hover, as: As = "div" }: { children: ReactNode; className?: string; hover?: boolean; as?: "div" | "section" }) {
  return <As className={cn("cc-card", hover && "cc-card-hover", className)}>{children}</As>;
}

export function Section({
  title, icon: Icon, action, children, className, count, bodyClassName,
}: {
  title: ReactNode;
  icon?: ComponentType<{ className?: string }>;
  action?: ReactNode;
  children: ReactNode;
  className?: string;
  count?: number;
  bodyClassName?: string;
}) {
  return (
    <Card as="section" className={cn("flex min-w-0 flex-col", className)}>
      <div className="flex items-center justify-between gap-2 border-b border-zinc-800/70 px-4 py-3">
        <div className="flex min-w-0 items-center gap-2">
          {Icon && <Icon className="h-4 w-4 shrink-0 text-indigo-300" />}
          <h2 className="truncate text-sm font-semibold text-zinc-100">{title}</h2>
          {count != null && count > 0 && <span className="rounded-full bg-zinc-800 px-1.5 py-0.5 text-[10px] font-semibold tabular-nums text-zinc-300">{count}</span>}
        </div>
        {action}
      </div>
      <div className={cn("min-h-0 flex-1 p-4", bodyClassName)}>{children}</div>
    </Card>
  );
}

export function Delta({ now, prev, invert, className }: { now: number; prev: number; invert?: boolean; className?: string }) {
  const c = change(now, prev);
  if (c == null) return null;
  const up = c > 0.001;
  const down = c < -0.001;
  const good = invert ? down : up;
  const bad = invert ? up : down;
  const Icon = up ? ArrowUpRight : down ? ArrowDownRight : Minus;
  return (
    <span className={cn("inline-flex items-center gap-0.5 text-[11px] font-semibold tabular-nums", good ? "text-emerald-400" : bad ? "text-rose-400" : "text-zinc-500", className)}>
      <Icon className="h-3 w-3" />
      {prev ? `${Math.abs(Math.round(c * 100))}%` : "new"}
    </span>
  );
}

export function Stat({
  label, value, sub, now, prev, to, icon: Icon, tone = "default", big, target,
}: {
  label: string;
  value: ReactNode;
  sub?: ReactNode;
  now?: number;
  prev?: number;
  to?: string;
  icon?: ComponentType<{ className?: string }>;
  tone?: "default" | "brand" | "good" | "warn" | "bad";
  big?: boolean;
  /** 0..1 progress toward a target, drawn as a thin bar. */
  target?: { value: number; label: string } | null;
}) {
  const body = (
    <Card hover={!!to} className={cn("relative h-full overflow-hidden p-4", tone === "brand" && "cc-glow")}>
      <div className="flex items-center justify-between gap-2">
        <span className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wider text-zinc-400">
          {Icon && <Icon className={cn("h-3.5 w-3.5", tone === "good" ? "text-emerald-400" : tone === "warn" ? "text-amber-400" : tone === "bad" ? "text-rose-400" : "text-indigo-300")} />}
          {label}
        </span>
        {now != null && prev != null && <Delta now={now} prev={prev} />}
      </div>
      <div className={cn("mt-2 font-semibold tabular-nums tracking-tight", big ? "text-3xl cc-gradient-text" : "text-2xl text-zinc-50")}>{value}</div>
      {sub && <div className="mt-1 text-xs text-zinc-500">{sub}</div>}
      {target && (
        <div className="mt-3">
          <div className="h-1.5 overflow-hidden rounded-full bg-zinc-800">
            <div className={cn("h-full rounded-full", target.value >= 1 ? "bg-emerald-400" : "cc-brand-bg")} style={{ width: `${Math.min(100, Math.max(3, target.value * 100))}%` }} />
          </div>
          <div className="mt-1 text-[11px] text-zinc-500">{target.label}</div>
        </div>
      )}
    </Card>
  );
  return to ? <Link to={to} className="block h-full">{body}</Link> : body;
}

/** Pill tabs ("Today · Week · Month"). */
export function Segmented<T extends string>({
  value, onChange, options, size = "md",
}: { value: T; onChange: (v: T) => void; options: Array<{ value: T; label: ReactNode }>; size?: "sm" | "md" }) {
  return (
    <div className="inline-flex rounded-xl border border-zinc-800 bg-zinc-900/60 p-0.5">
      {options.map((o) => (
        <button
          key={o.value}
          type="button"
          onClick={() => onChange(o.value)}
          className={cn(
            "rounded-[10px] font-medium transition-colors",
            size === "sm" ? "px-2.5 py-1 text-xs" : "px-3 py-1.5 text-sm",
            value === o.value ? "bg-indigo-500/20 text-indigo-100 ring-1 ring-indigo-400/30" : "text-zinc-400 hover:text-zinc-100",
          )}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}

/** A tiny line chart for a run of numbers. */
export function Spark({ values, className, color = "#818cf8", fill = true }: { values: number[]; className?: string; color?: string; fill?: boolean }) {
  const w = 120, h = 32;
  if (!values.length) return <svg viewBox={`0 0 ${w} ${h}`} className={className} />;
  const max = Math.max(1, ...values);
  const step = values.length > 1 ? w / (values.length - 1) : w;
  const pts = values.map((v, i) => [i * step, h - 2 - (v / max) * (h - 6)] as const);
  const line = pts.map(([x, y], i) => `${i ? "L" : "M"}${x.toFixed(1)},${y.toFixed(1)}`).join(" ");
  return (
    <svg viewBox={`0 0 ${w} ${h}`} preserveAspectRatio="none" className={cn("h-8 w-full", className)} aria-hidden="true">
      {fill && <path d={`${line} L${w},${h} L0,${h} Z`} fill={color} opacity={0.12} />}
      <path d={line} fill="none" stroke={color} strokeWidth={1.6} strokeLinejoin="round" strokeLinecap="round" vectorEffect="non-scaling-stroke" />
    </svg>
  );
}

/** Round check that pops when ticked. */
export function TickButton({ done, onToggle, title, disabled }: { done: boolean; onToggle: () => void; title?: string; disabled?: boolean }) {
  const [pop, setPop] = useState(false);
  return (
    <button
      type="button"
      title={title ?? (done ? "Mark as not done" : "Mark done")}
      disabled={disabled}
      onClick={(e) => {
        e.stopPropagation();
        e.preventDefault();
        if (!done) { setPop(true); setTimeout(() => setPop(false), 340); }
        onToggle();
      }}
      className={cn(
        "flex h-5 w-5 shrink-0 items-center justify-center rounded-full border transition-colors",
        done ? "border-emerald-400 bg-emerald-400 text-zinc-950" : "border-zinc-600 text-transparent hover:border-indigo-400 hover:text-indigo-300",
        pop && "cc-pop",
      )}
    >
      <Check className="h-3 w-3" strokeWidth={3} />
    </button>
  );
}

export function Pill({ children, className, color }: { children: ReactNode; className?: string; color?: string | null }) {
  return (
    <span className={cn("inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-medium ring-1 ring-inset", color ? softClass(color) : "bg-zinc-800/80 text-zinc-300 ring-zinc-700/60", className)}>
      {children}
    </span>
  );
}

export function Empty({ icon: Icon, title, children, action }: { icon?: ComponentType<{ className?: string }>; title: ReactNode; children?: ReactNode; action?: ReactNode }) {
  return (
    <div className="flex flex-col items-center justify-center gap-2 px-4 py-8 text-center">
      {Icon && (
        <div className="flex h-10 w-10 items-center justify-center rounded-2xl bg-indigo-500/10 ring-1 ring-indigo-400/20">
          <Icon className="h-5 w-5 text-indigo-300" />
        </div>
      )}
      <div className="text-sm font-medium text-zinc-200">{title}</div>
      {children && <div className="max-w-sm text-xs leading-relaxed text-zinc-500">{children}</div>}
      {action && <div className="mt-1">{action}</div>}
    </div>
  );
}

/** Horizontal share bar ("how much of the whole"). */
export function ShareBar({ value, max, className }: { value: number; max: number; className?: string }) {
  const w = max > 0 ? Math.max(value > 0 ? 3 : 0, (value / max) * 100) : 0;
  return (
    <div className={cn("h-1.5 w-full overflow-hidden rounded-full bg-zinc-800/80", className)}>
      <div className="h-full rounded-full cc-brand-bg" style={{ width: `${w}%` }} />
    </div>
  );
}

export const STATUS_LABEL: Record<string, string> = { idea: "Idea", testing: "Testing", active: "Active", paused: "Paused" };
export const STATUS_COLOR: Record<string, string> = { idea: "zinc", testing: "amber", active: "emerald", paused: "slate" };
